import type { TypedDocumentNode } from "@graphql-typed-document-node/core"
import { type UseInfiniteQueryResult, useInfiniteQuery } from "@tanstack/react-query"
import { useAuth } from "~/features/auth/useAuth"
import type { LibrarySortType } from "~/graphql/generated/graphql"
import { useGraphQLClient } from "~/shared/api/GraphQLClientContext"
import { nextLibraryCursor, type PageInfoLike } from "~/shared/lib/pagination"

interface LibraryVariables {
  cursor?: string | undefined
  sortBy?: LibrarySortType | undefined
}

interface LibraryEdgeLike {
  readonly node: { readonly asset: { readonly __typename?: string } }
}

type LibraryResult = {
  user?: {
    libraryV2?: {
      edges: readonly LibraryEdgeLike[]
      pageInfo?: PageInfoLike | null
    } | null
  } | null
}

/**
 * One hook behind every library list. Sort is part of the query key, so changing
 * it starts a fresh cursor chain rather than trying to reuse a stale one.
 */
export const useLibraryQuery = <TResult extends LibraryResult>(
  key: string,
  document: TypedDocumentNode<TResult, LibraryVariables>,
  sortBy: LibrarySortType,
): UseInfiniteQueryResult<{ pages: TResult[] }, Error> => {
  const client = useGraphQLClient()
  const { status } = useAuth()

  return useInfiniteQuery({
    queryKey: ["library", key, sortBy],
    queryFn: ({ pageParam }) => client.request(document, { cursor: pageParam, sortBy }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextLibraryCursor,
    enabled: status === "signedIn",
  })
}
